import React, { useState, useEffect } from 'react';
import { collection, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

/**
 * Profile component for managing user preferences
 * Allows users to update their shift pattern and country preferences
 */
const Profile = ({ currentUser }) => {
  const [username, setUsername] = useState('');
  const [country, setCountry] = useState('Ireland');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [patterns, setPatterns] = useState({});
  const [selectedPattern, setSelectedPattern] = useState('');
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Fetch user data and available shift patterns
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        // Get user document
        const userRef = collection(db, 'users');
        const q = query(userRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          setUsername(userData.username || '');
          setCountry(userData.country || 'Ireland');
          
          // Load shift preferences if they exist
          if (userData.shiftPreferences) {
            const { company, pattern, shift } = userData.shiftPreferences;
            setSelectedCompany(company || '');
            setSelectedPattern(pattern || '');
            setSelectedShift(shift || '');
          }
        }

        // Fetch companies and patterns
        await fetchCompaniesAndPatterns();
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, navigate]);

  // Fetch companies and shift patterns
  const fetchCompaniesAndPatterns = async () => {
    try {
      // Fetch companies
      const companiesRef = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesRef);
      const companiesList = companiesSnapshot.docs.map(doc => doc.data().name);
      
      // Add Default company if not already in list
      if (!companiesList.includes('Default')) {
        companiesList.push('Default');
      }
      
      setCompanies(companiesList);
      
      // Fetch shift patterns
      const shiftsRef = collection(db, 'shiftPatterns');
      const querySnapshot = await getDocs(shiftsRef);
      
      const companyPatterns = {};
      const shiftOptions = new Set();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const companyName = data.company;
        const patternName = data.patternName;
        const shiftName = data.shift;
        
        if (!companyPatterns[companyName]) {
          companyPatterns[companyName] = [];
        }
        
        if (!companyPatterns[companyName].includes(patternName)) {
          companyPatterns[companyName].push(patternName);
        }
        
        shiftOptions.add(shiftName);
      });
      
      setPatterns(companyPatterns);
      setShifts(Array.from(shiftOptions));
    } catch (error) {
      console.error('Error fetching companies and patterns:', error);
      setError('Failed to load companies and patterns. Please try again.');
    }
  };

  // Handle company change
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    
    // Reset pattern when company changes
    if (patterns[company] && patterns[company].length > 0) {
      setSelectedPattern(patterns[company][0]);
    } else {
      setSelectedPattern('');
    }
  };

  // Handle pattern change
  const handlePatternChange = (e) => {
    setSelectedPattern(e.target.value);
  };

  // Handle shift change
  const handleShiftChange = (e) => {
    setSelectedShift(e.target.value);
  };

  // Handle country change
  const handleCountryChange = (e) => {
    setCountry(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (!selectedCompany || !selectedPattern || !selectedShift) {
      setError('Please select a company, pattern, and shift.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Get user document reference
      const userRef = collection(db, 'users');
      const q = query(userRef, where('email', '==', currentUser.email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDocRef = doc(db, 'users', querySnapshot.docs[0].id);
        
        // Update user preferences
        await updateDoc(userDocRef, {
          country: country,
          shiftPreferences: {
            company: selectedCompany,
            pattern: selectedPattern,
            shift: selectedShift
          },
          updatedAt: new Date()
        });
        
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('User document not found. Please try logging in again.');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      setError('Failed to update preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render profile form
  return (
    <div className="d-flex justify-content-center">
      <Card className="w-100" style={{ maxWidth: '800px' }}>
        <Card.Header>
          <h2 className="text-center mb-0">User Profile</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">Profile updated successfully!</Alert>}
          
          <Form onSubmit={handleSubmit}>
            {/* User Information */}
            <h4 className="mb-3">User Information</h4>
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                disabled
              />
              <Form.Text className="text-muted">
                Username cannot be changed.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="formCountry">
              <Form.Label>Country (for Bank Holidays)</Form.Label>
              <Form.Select
                value={country}
                onChange={handleCountryChange}
                required
              >
                <option value="Ireland">Ireland</option>
                <option value="UK">UK</option>
              </Form.Select>
            </Form.Group>
            
            {/* Shift Preferences */}
            <h4 className="mb-3 mt-4">Shift Preferences</h4>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="formCompany">
                  <Form.Label>Company</Form.Label>
                  <Form.Select
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map((company, idx) => (
                      <option key={idx} value={company}>
                        {company}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3" controlId="formPattern">
                  <Form.Label>Pattern</Form.Label>
                  <Form.Select
                    value={selectedPattern}
                    onChange={handlePatternChange}
                    required
                    disabled={!patterns[selectedCompany] || patterns[selectedCompany].length === 0}
                  >
                    <option value="">Select Pattern</option>
                    {patterns[selectedCompany]?.map((pattern, idx) => (
                      <option key={idx} value={pattern}>
                        {pattern}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3" controlId="formShift">
                  <Form.Label>Shift</Form.Label>
                  <Form.Select
                    value={selectedShift}
                    onChange={handleShiftChange}
                    required
                  >
                    <option value="">Select Shift</option>
                    {shifts.map((shift, idx) => (
                      <option key={idx} value={shift}>
                        {shift}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-between mt-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/')}
              >
                Back to Calendar
              </Button>
              
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Profile;
